import { getAccessToken, getCurrentUser } from './authService'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { handleSessionReplaced } from '../utils/sessionReplacedHandler'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

async function normalizeSlipImageBlob(blob: Blob): Promise<Blob> {
  if (blob.type.startsWith('image/')) return blob

  const bytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer())
  const isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff
  const isPng = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47
  const isGif = bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38
  const isWebp =
    bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50

  const inferredType = isJpeg
    ? 'image/jpeg'
    : isPng
      ? 'image/png'
      : isGif
        ? 'image/gif'
        : isWebp
          ? 'image/webp'
          : ''

  if (!inferredType) {
    throw new Error('ไฟล์สลิปไม่ใช่รูปภาพที่รองรับ')
  }

  return blob.slice(0, blob.size, inferredType)
}

const blobToDataUrl = async (blob: Blob): Promise<string> => {
  const normalizedBlob = await normalizeSlipImageBlob(blob)

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string' && reader.result) {
        resolve(reader.result)
        return
      }
      reject(new Error('ไม่สามารถแปลงรูปสลิปได้'))
    }
    reader.onerror = () => reject(new Error('ไม่สามารถแปลงรูปสลิปได้'))
    reader.readAsDataURL(normalizedBlob)
  })
}

interface WalletWithdrawalResponse {
  success: boolean
  data?: {
    _id?: string
    transactionId?: string
    approval?: {
      required?: boolean
      status?: string
    }
    status?: string
    strictTurnoverEnforcement?: boolean
    canForceForfeitBonus?: boolean
    totalRemainingWagering?: number
    bonuses?: Array<{
      type?: string
      amount?: number
      wageringRemaining?: number
      note?: string
    }>
    cooldownSeconds?: number
    retryAfterSeconds?: number
    retryAt?: string
    lastTransactionId?: string | null
    lastWithdrawalStatus?: string | null
    userMessage?: string
  }
  error?: string
  message?: string
  code?: string
}

export interface WalletDepositFallback {
  allowed: boolean
  targetChannel: 'bank_transfer'
  amount: number
  reasonCode: string
  userMessage?: string
}

export interface WalletDepositResponse {
  success: boolean
  data?: WalletTransaction & {
    approval?: {
      required?: boolean
      status?: string
    }
    slipVerification?: {
      required?: boolean
      status?: string
      provider?: string
    }
    metadata?: {
      depositMode?: string
      submittedSlip?: {
        imageUrl?: string
      }
      depositRouting?: {
        sourceChannel?: string
        autoCreditDecision?: string
        candidateStatus?: string
        reviewReason?: string
      }
      paymentGateway?: {
        provider?: string
        channel?: string
        externalRefId?: string
        providerRefId?: string
        externalUserId?: string
        providerStatus?: string
        confirmStatus?: string
        qrRawData?: string
        qrImage?: string
        qrUrl?: string
        qrcodePayload?: {
          qrcode_text?: string
          link?: string
        }
        bank?: string
        accountNumber?: string
        payerBankCode?: string
        payerBankName?: string
        payerAccountNumber?: string
        payerAccountName?: string
        requestedAmount?: number
        createdAt?: string
        providerExpiresAt?: string
        providerExpiresInSeconds?: number
        reuseWindowMinutes?: number
        reuseExpiresAt?: string
        lastReusedAt?: string
        wasReused?: boolean
        requestedAmountAtReuse?: number
      }
    }
  }
  error?: string
  message?: string
  code?: string
  recoveredExistingDeposit?: boolean
  fallback?: WalletDepositFallback
}

function getDepositFailureMessage(
  payload?: {
    code?: string
    message?: string
    error?: string
    data?: unknown
  },
  fallback = 'เกิดข้อผิดพลาดในการส่งรายการฝากเงิน'
): string {
  const code = String(payload?.code || '').trim().toUpperCase()
  const rawMessage = String(payload?.message || payload?.error || '').trim()
  const lowerMessage = rawMessage.toLowerCase()

  if (
    code === 'DEPOSIT_DUPLICATE_EVIDENCE' ||
    lowerMessage.includes('deposit evidence is already linked') ||
    lowerMessage.includes('duplicate_evidence') ||
    lowerMessage.includes('สลิปซ้ำ') ||
    lowerMessage.includes('duplicate slip')
  ) {
    return 'ระบบพบว่าข้อมูลการโอนนี้ถูกผูกกับรายการฝากเดิมในระบบแล้ว กรุณาตรวจสอบประวัติรายการฝากหรือติดต่อเจ้าหน้าที่'
  }

  if (lowerMessage.includes('duplicate entry')) {
    return 'ระบบพบข้อมูลซ้ำในรายการฝากนี้ กรุณาตรวจสอบประวัติรายการฝากหรือลองใหม่อีกครั้ง'
  }

  if (
    code === 'DEPOSIT_ACCOUNT_SMS_ONLY' ||
    lowerMessage.includes('configured for sms only')
  ) {
    return 'บัญชีรับฝากนี้ตั้งค่าเป็น SMS เท่านั้น จึงไม่สามารถแนบสลิปได้'
  }

  if (
    code === 'DEPOSIT_ACCOUNT_SLIP_REQUIRED' ||
    lowerMessage.includes('requires slip attachment')
  ) {
    return 'บัญชีรับฝากนี้กำหนดให้แนบสลิปก่อนส่งรายการ'
  }

  if (rawMessage) {
    return rawMessage
  }

  return fallback
}

export function assertDepositResponseMatchesDraft(result: WalletDepositResponse, draftId?: string) {
  const normalizedDraftId = String(draftId || '').trim()
  if (!normalizedDraftId) return
  if (result.recoveredExistingDeposit) return

  const returnedReference = String(result.data?.details?.reference || '').trim()
  if (returnedReference && returnedReference !== normalizedDraftId) {
    throw new Error('สลิปนี้ถูกผูกกับรายการฝากเดิมในระบบแล้ว กรุณาตรวจสอบประวัติรายการฝากหรือใช้สลิปใหม่')
  }
}

export interface WalletTransaction {
  _id: string
  transactionId?: string
  tenantId: string
  userId: string
  type: 'deposit' | 'withdraw' | 'bonus' | 'transfer_in' | 'transfer_out' | 'refund' | 'adjustment' | 'fee' | 'bet' | 'win' | 'cancel'
  category?: string
  amount: number
  currency?: string
  status: 'pending' | 'processing' | 'reserved' | 'completed' | 'failed' | 'cancelled' | 'expired' | 'locked'
  details?: {
    description?: string
    reference?: string
    externalId?: string
    bankAccount?: {
      bankCode?: string
      accountNumber?: string
      accountName?: string
    }
  }
  approval?: {
    required?: boolean
    status?: string
    approvedBy?: string
    approvedAt?: string
    rejectedBy?: string
    rejectedAt?: string
    rejectionReason?: string
  }
  slipVerification?: {
    required?: boolean
    status?: string
    provider?: string
    providerTransactionId?: string
    slipData?: {
      imageUrl?: string
      qrData?: string
      verifiedAt?: string
      verifiedBy?: string
    }
  }
  metadata?: {
    notes?: string
    depositMethod?: string
    selectedDepositAccountId?: string
    selectedDepositAccountNumber?: string
    selectedDepositAccountName?: string
    selectedDepositBankCode?: string
    selectedDepositBankName?: string
    selectedDepositSourceMode?: 'both' | 'slip_only' | 'sms_only'
    receiverAccount?: {
      accountId?: string
      bankCode?: string
      bankName?: string
      accountNumber?: string
      accountName?: string
      depositSourceMode?: 'both' | 'slip_only' | 'sms_only'
    }
    submittedSlip?: {
      imageUrl?: string
      qrData?: string
      bankCode?: string
      accountNumber?: string
      uploadedAt?: string
    }
    depositRouting?: {
      sourceChannel?: string
      autoCreditDecision?: string
      candidateStatus?: string
      reviewReason?: string
    }
    paymentGateway?: {
      provider?: string
      channel?: string
      externalRefId?: string
      providerRefId?: string
      externalUserId?: string
      providerStatus?: string
      confirmStatus?: string
      qrRawData?: string
      qrImage?: string
      qrUrl?: string
      bank?: string
      accountNumber?: string
      payerBankCode?: string
      payerBankName?: string
      payerAccountNumber?: string
      payerAccountName?: string
      requestedAmount?: number
      createdAt?: string
      providerExpiresAt?: string
      providerExpiresInSeconds?: number
      reuseWindowMinutes?: number
      reuseExpiresAt?: string
      lastReusedAt?: string
      wasReused?: boolean
      requestedAmountAtReuse?: number
    }
  }
  audit?: {
    createdAt?: string
    updatedAt?: string
  }
  createdAt?: string
  updatedAt?: string
}

interface WalletTransactionListResponse {
  success: boolean
  data?: {
    transactions?: WalletTransaction[]
    pagination?: {
      page: number
      limit: number
      total: number
      pages: number
    }
  }
  error?: string
  message?: string
  code?: string
}

interface WalletTransactionDetailResponse {
  success: boolean
  data?: {
    transaction?: WalletTransaction
  }
  error?: string
  message?: string
  code?: string
}

function resolveTenantAndUser() {
  const user = getCurrentUser()
  const tenantId = user?.tenantId || getRuntimeTenantIdSync() || localStorage.getItem('tenantId') || ''
  const userId = user?.id || (user as { _id?: string } | null)?._id || ''
  return { tenantId, userId }
}

export async function createDeposit(payload: {
  amount: number
  paymentMethod: 'bank_transfer' | 'sandspay' | 'payment_gateway'
  reference?: string
  slipData?: {
    imageUrl?: string
    bankCode?: string
    accountNumber?: string
    qrData?: string
  }
  metadata?: Record<string, unknown>
}): Promise<WalletDepositResponse> {
  const token = getAccessToken()
  const { tenantId, userId } = resolveTenantAndUser()

  if (!tenantId || !userId || !token) {
    return { success: false, message: 'ไม่พบข้อมูลผู้ใช้' }
  }

  try {
    const res = await fetch(
      withApiBaseUrl(`/api/v1/wallet/tenant/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/transactions/deposit`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
          'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : '',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      }
    )

    const data = (await res.json().catch(() => ({}))) as WalletDepositResponse
    if (res.status === 401 && data?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      return { success: false, message: 'SESSION_REPLACED' }
    }

    if (!res.ok) {
      return {
        success: false,
        message: getDepositFailureMessage({
          message: data?.message || data?.error || `HTTP ${res.status}`,
          error: data?.error,
          code: data?.code,
          data: data?.data as unknown as Record<string, unknown> | undefined,
        }),
        error: data?.error,
        code: data?.code,
        data: data?.data,
        fallback: data?.fallback,
      }
    }

    return data
  } catch (error) {
    return {
      success: false,
      message: getDepositFailureMessage({
        message: error instanceof Error ? error.message : '',
      }),
    }
  }
}

export async function createWithdrawal(payload: {
  amount: number
  bankAccount: {
    bankCode: string
    accountNumber: string
    accountName: string
  }
  reason?: string
}): Promise<WalletWithdrawalResponse> {
  const token = getAccessToken()
  const { tenantId, userId } = resolveTenantAndUser()

  if (!tenantId || !userId || !token) {
    return { success: false, message: 'ไม่พบข้อมูลผู้ใช้' }
  }

  try {
    const res = await fetch(
      withApiBaseUrl(`/api/v1/wallet/tenant/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/transactions/withdraw`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
          'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : '',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      }
    )

    const data = (await res.json().catch(() => ({}))) as WalletWithdrawalResponse
    if (res.status === 401 && data?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      return { success: false, message: 'SESSION_REPLACED' }
    }

    if (!res.ok) {
      return {
        success: false,
        message: data?.message || data?.error || `HTTP ${res.status}`,
        error: data?.error,
        code: data?.code,
        data: data?.data,
      }
    }

    return data
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'เกิดข้อผิดพลาดในการส่งคำขอถอนเงิน',
    }
  }
}

export async function getUserTransactions(params?: {
  type?: WalletTransaction['type']
  status?: string
  limit?: number
  page?: number
}): Promise<WalletTransactionListResponse> {
  const token = getAccessToken()
  const { tenantId, userId } = resolveTenantAndUser()

  if (!tenantId || !userId || !token) {
    return { success: false, message: 'ไม่พบข้อมูลผู้ใช้' }
  }

  try {
    const search = new URLSearchParams()
    if (params?.type) search.set('type', params.type)
    if (params?.status) search.set('status', params.status)
    if (params?.limit) search.set('limit', String(params.limit))
    if (params?.page) search.set('page', String(params.page))

    const query = search.toString()
    const res = await fetch(
      withApiBaseUrl(`/api/v1/wallet/tenant/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/transactions${query ? `?${query}` : ''}`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
          'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : '',
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
        cache: 'no-store',
        credentials: 'include',
      }
    )

    const data = (await res.json().catch(() => ({}))) as WalletTransactionListResponse
    if (res.status === 401 && data?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      return { success: false, message: 'SESSION_REPLACED' }
    }

    if (!res.ok) {
      return {
        success: false,
        message: data?.message || data?.error || `HTTP ${res.status}`,
        error: data?.error,
        code: data?.code,
        data: data?.data,
      }
    }

    return data
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'เกิดข้อผิดพลาดในการดึงประวัติธุรกรรม',
    }
  }
}

export async function getTransactionDetails(transactionId: string): Promise<WalletTransactionDetailResponse> {
  const token = getAccessToken()
  const { tenantId } = resolveTenantAndUser()

  if (!tenantId || !transactionId || !token) {
    return { success: false, message: 'ไม่พบข้อมูลผู้ใช้หรือรายการฝาก' }
  }

  try {
    const res = await fetch(
      withApiBaseUrl(`/api/v1/wallet/tenant/${encodeURIComponent(tenantId)}/transactions/${encodeURIComponent(transactionId)}`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
          'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : '',
        },
        credentials: 'include',
      }
    )

    const data = (await res.json().catch(() => ({}))) as WalletTransactionDetailResponse
    if (res.status === 401 && data?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      return { success: false, message: 'SESSION_REPLACED' }
    }

    if (!res.ok) {
      return {
        success: false,
        message: getDepositFailureMessage({
          message: data?.message || data?.error || `HTTP ${res.status}`,
          error: data?.error,
          code: data?.code,
          data: data?.data as unknown as Record<string, unknown> | undefined,
        }, 'เกิดข้อผิดพลาดในการแนบสลิปภายหลัง'),
        error: data?.error,
        code: data?.code,
        data: data?.data,
      }
    }

    return data
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'เกิดข้อผิดพลาดในการดึงข้อมูลรายการฝาก',
    }
  }
}

export async function getTransactionSlipPreviewUrl(transactionId: string): Promise<string> {
  const token = getAccessToken()
  const { tenantId } = resolveTenantAndUser()

  if (!tenantId || !transactionId || !token) {
    throw new Error('ไม่พบข้อมูลผู้ใช้หรือรายการฝาก')
  }

  const res = await fetch(
    withApiBaseUrl(`/api/v1/wallet/tenant/${encodeURIComponent(tenantId)}/transactions/${encodeURIComponent(transactionId)}/slip-image`),
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
        'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : '',
      },
      credentials: 'include',
    }
  )

  if (!res.ok) {
    const errorData = (await res.json().catch(() => ({}))) as { message?: string; error?: string; code?: string }
    if (res.status === 401 && errorData?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      throw new Error('SESSION_REPLACED')
    }
    throw new Error(errorData?.message || errorData?.error || `HTTP ${res.status}`)
  }

  const blob = await res.blob()
  return blobToDataUrl(blob)
}

export async function refreshPaymentGatewayTransaction(transactionId: string): Promise<WalletTransactionDetailResponse> {
  const token = getAccessToken()
  const { tenantId, userId } = resolveTenantAndUser()

  if (!tenantId || !userId || !transactionId || !token) {
    return { success: false, message: 'ไม่พบข้อมูลผู้ใช้หรือรายการฝาก' }
  }

  try {
    const res = await fetch(
      withApiBaseUrl(
        `/api/v1/wallet/tenant/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/transactions/${encodeURIComponent(transactionId)}/payment-gateway/refresh`
      ),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
          'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : '',
        },
        credentials: 'include',
      }
    )

    const data = (await res.json().catch(() => ({}))) as WalletTransactionDetailResponse
    if (res.status === 401 && data?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      return { success: false, message: 'SESSION_REPLACED' }
    }

    if (!res.ok) {
      return {
        success: false,
        message: data?.message || data?.error || `HTTP ${res.status}`,
        error: data?.error,
      }
    }

    return data
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'เกิดข้อผิดพลาดในการรีเฟรชสถานะ Payment',
    }
  }
}

export async function attachSlipToDepositTransaction(
  transactionId: string,
  payload: {
    slipData: {
      imageUrl?: string
      bankCode?: string
      accountNumber?: string
      qrData?: string
    }
  }
): Promise<WalletDepositResponse> {
  const token = getAccessToken()
  const { tenantId, userId } = resolveTenantAndUser()

  if (!tenantId || !userId || !transactionId || !token) {
    return { success: false, message: 'ไม่พบข้อมูลผู้ใช้หรือรายการฝาก' }
  }

  try {
    const res = await fetch(
      withApiBaseUrl(`/api/v1/wallet/tenant/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/transactions/${encodeURIComponent(transactionId)}/attach-slip`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
          'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : '',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      }
    )

    const data = (await res.json().catch(() => ({}))) as WalletDepositResponse
    if (res.status === 401 && data?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      return { success: false, message: 'SESSION_REPLACED' }
    }

    if (!res.ok) {
      return {
        success: false,
        message: data?.message || data?.error || `HTTP ${res.status}`,
        error: data?.error,
      }
    }

    return data
  } catch (error) {
    return {
      success: false,
      message: getDepositFailureMessage({
        message: error instanceof Error ? error.message : '',
      }, 'เกิดข้อผิดพลาดในการแนบสลิปภายหลัง'),
    }
  }
}
